<template>
    <div>
        <!-- Initial Page Loading Overlay -->
        <v-overlay :value="initialLoading" z-index="999">
            <div class="d-flex flex-column align-center">
                <v-progress-circular indeterminate size="64" color="primary" width="6"></v-progress-circular>
                <div class="mt-4 text-h6 white--text">Loading Projects...</div>
            </div>
        </v-overlay>

        <v-container class="mt-10 pb-12"
            style="min-width: 1200px; max-width: 1600px; padding-left: 24px; padding-right: 24px;">
        <!-- Header Section -->
        <div class="mb-6">
            <div class="d-flex align-center justify-space-between mb-4">
                <div>
                    <h1 class="text-h4 font-weight-bold mb-2">Project Dashboard</h1>
                    <p class="text-subtitle-1 grey--text mb-0">Overview of all companies and their initiatives</p>
                </div>
                <div class="d-flex" style="gap: 8px;">
                    <v-btn color="primary" outlined :loading="refreshing" @click="refreshData" class="text-none">
                        <v-icon left>mdi-refresh</v-icon>
                        Refresh
                    </v-btn>
                    <v-btn v-if="canCreateCompany" color="primary" @click="openCreateCompanyDialog" class="text-none">
                        <v-icon left>mdi-plus</v-icon>
                        Add Company
                    </v-btn>
                </div>
            </div>
        </div>

        <!-- Stats Cards -->
        <v-row class="mt-4 mb-4">
            <v-col cols="12" sm="4">
                <v-card class="stat-card" elevation="0">
                    <v-card-text>
                        <div class="d-flex justify-space-between align-center">
                            <div>
                                <div class="stat-label">Total Companies</div>
                                <div v-if="loadingCompanies" class="stat-value">
                                    <v-skeleton-loader type="text" width="60"></v-skeleton-loader>
                                </div>
                                <div v-else class="stat-value">{{ totalCompanies }}</div>
                            </div>
                            <div class="stat-icon primary-icon">
                                <v-icon color="primary" size="32">mdi-domain</v-icon>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" sm="4">
                <v-card class="stat-card" elevation="0">
                    <v-card-text>
                        <div class="d-flex justify-space-between align-center">
                            <div>
                                <div class="stat-label">Active Goals</div>
                                <div v-if="loadingGoals" class="stat-value">
                                    <v-skeleton-loader type="text" width="60"></v-skeleton-loader>
                                </div>
                                <div v-else class="stat-value">{{ activeGoals }}</div>
                            </div>
                            <div class="stat-icon success-icon">
                                <v-icon color="purple" size="32">mdi-target</v-icon>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" sm="4">
                <v-card class="stat-card" elevation="0">
                    <v-card-text>
                        <div class="d-flex justify-space-between align-center">
                            <div>
                                <div class="stat-label">Bottlenecks</div>
                                <div v-if="loadingBottlenecks" class="stat-value">
                                    <v-skeleton-loader type="text" width="60"></v-skeleton-loader>
                                </div>
                                <div v-else class="stat-value">{{ allBottlenecks.length }}</div>
                            </div>
                            <div class="stat-icon error-icon">
                                <v-icon color="error" size="32">mdi-alert-circle</v-icon>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Active Bottlenecks Section -->
        <v-card v-if="loadingBottlenecks" class="bottlenecks-card mb-6" elevation="0">
            <v-card-title class="bottlenecks-header">
                <div class="d-flex align-center">
                    <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
                    Loading Bottlenecks...
                </div>
            </v-card-title>
            <v-card-text class="bottlenecks-content">
                <v-skeleton-loader type="list-item-three-line"></v-skeleton-loader>
            </v-card-text>
        </v-card>

        <v-card v-else-if="bottlenecks.length > 0" class="bottlenecks-card mb-6" elevation="0">
            <v-card-title class="bottlenecks-header">
                <div class="d-flex align-center">
                    <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
                    Active Bottlenecks ({{ allBottlenecks.length }})
                </div>
                <v-spacer></v-spacer>
                <v-btn v-if="allBottlenecks.length > 3" text small color="error"
                    @click="showAllBottlenecks = !showAllBottlenecks">
                    {{ showAllBottlenecks ? 'Show Less' : `Show All (${allBottlenecks.length})` }}
                    <v-icon right small>{{ showAllBottlenecks ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
            </v-card-title>
            <v-card-text class="bottlenecks-content" :class="{ 'bottlenecks-scrollable': showAllBottlenecks }">
                <div v-for="bottleneck in bottlenecks" :key="bottleneck.id" class="bottleneck-item">
                    <div class="bottleneck-info">
                        <div class="bottleneck-title">{{ bottleneck.name }}</div>
                        <div class="bottleneck-meta">
                            <v-icon small class="mr-1">mdi-domain</v-icon>
                            <span class="company-name-text">{{ getCompanyName(bottleneck) }}</span>
                            <v-icon small class="mx-2">mdi-account</v-icon>
                            <span v-if="bottleneck.assignees && bottleneck.assignees.length > 0">
                                {{ getAssigneeNames(bottleneck.assignees) }}
                            </span>
                            <span v-else>Unassigned</span>
                            <v-icon small class="mx-2">mdi-calendar</v-icon>
                            {{ formatDate(bottleneck.due_date) }} ({{ getOverdueDays(bottleneck.due_date) }})
                        </div>
                    </div>
                    <v-chip :color="getStatusColor(bottleneck.status_id)" small dark>
                        {{ getStatusText(bottleneck.status_id) }}
                    </v-chip>
                </div>
            </v-card-text>
        </v-card>

        <!-- All Companies Section -->
        <div class="section-header mb-4">
            <h2 class="section-title">All Companies</h2>
            <v-text-field v-model="searchQuery" placeholder="Search companies..." prepend-inner-icon="mdi-magnify"
                outlined dense hide-details class="search-field"></v-text-field>
        </div>

        <!-- Stage Progression Timeline -->
        <v-card class="timeline-card mb-6" elevation="0">
            <v-card-text class="pa-6">
                <div class="timeline-header mb-4">
                    <h3 class="timeline-title">Stage Progression Timeline</h3>
                    <p class="timeline-subtitle">{{ totalInProgressCompanies }} companies progressing from Planned to Live & Scaling</p>
                </div>
                
                <div class="stage-timeline">
                    <div 
                        v-for="(stage, index) in timelineStages" 
                        :key="stage.key || 'uncategorised'"
                        class="timeline-stage-wrapper"
                    >
                        <div class="timeline-connector" v-if="index < timelineStages.length - 1"></div>
                        
                        <v-menu offset-y :close-on-content-click="false" max-width="400">
                            <template v-slot:activator="{ on, attrs }">
                                <div 
                                    class="timeline-stage"
                                    v-bind="attrs"
                                    v-on="on"
                                    style="cursor: pointer;"
                                >
                                    <div 
                                        class="stage-circle"
                                        :class="{ 'has-companies': stage.count > 0 }"
                                        :style="{ 
                                            borderColor: stage.color,
                                            backgroundColor: stage.count > 0 ? stage.color : 'transparent',
                                            color: stage.count > 0 ? '#fff' : stage.color
                                        }"
                                    >
                                        <span v-if="stage.count > 0" class="stage-count">{{ stage.count }}</span>
                                    </div>
                                    <div class="stage-label" :style="{ color: stage.count > 0 ? stage.color : '#9e9e9e' }">
                                        {{ stage.name }}
                                    </div>
                                </div>
                            </template>
                            
                            <v-card class="stage-dropdown-menu" elevation="8">
                                <v-card-title class="pa-3 pb-2" style="font-size: 16px;">
                                    <v-icon :color="stage.color" class="mr-2">{{ stage.icon }}</v-icon>
                                    {{ stage.name }}
                                </v-card-title>
                                <v-card-subtitle class="pa-3 pt-0 pb-2" style="font-size: 13px;">
                                    {{ stage.count }} {{ stage.count === 1 ? 'company' : 'companies' }} progressing from Planned to Live & Scaling
                                </v-card-subtitle>
                                <v-divider></v-divider>
                                
                                <v-list dense class="pa-0" style="max-height: 400px; overflow-y: auto;">
                                    <template v-if="stage.companies && stage.companies.length > 0">
                                        <v-list-item 
                                            v-for="company in stage.companies" 
                                            :key="company.id"
                                            @click="viewCompany(company)"
                                            class="stage-company-item"
                                        >
                                            <v-list-item-avatar size="8" class="mr-2" style="min-width: 8px;">
                                                <div 
                                                    style="width: 8px; height: 8px; border-radius: 50%;"
                                                    :style="{ backgroundColor: stage.color }"
                                                ></div>
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title style="font-size: 14px;">{{ company.name }}</v-list-item-title>
                                            </v-list-item-content>
                                            <v-list-item-action class="ma-0">
                                                <span style="font-size: 13px; color: #666;">{{ Math.round(getCompanyProgress(company.id)) }}%</span>
                                            </v-list-item-action>
                                        </v-list-item>
                                    </template>
                                    <v-list-item v-else>
                                        <v-list-item-content>
                                            <v-list-item-title class="text-center grey--text">No companies at this stage</v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list>
                            </v-card>
                        </v-menu>
                    </div>
                </div>
            </v-card-text>
        </v-card>

        <!-- Companies by Stage (Kanban View) -->
        <div v-if="loadingCompanies" class="mb-6">
            <v-skeleton-loader type="article, article, article"></v-skeleton-loader>
        </div>

        <div v-else>
            <div v-for="stage in companiesByStageData" :key="stage.key || 'uncategorised'" class="stage-section mb-4">
                <div 
                    class="stage-header"
                    :style="{ borderLeftColor: stage.color }"
                    @click="toggleStage(stage.key)"
                >
                    <div class="d-flex align-center">
                        <v-icon class="mr-2" :color="stage.color">{{ expandedStages[stage.key] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                        <v-icon class="mr-2" :color="stage.color">{{ stage.icon }}</v-icon>
                        <h3 class="stage-title">{{ stage.name.toUpperCase() }}</h3>
                        <v-chip small class="ml-3" :color="stage.color" dark>{{ stage.count }} items</v-chip>
                    </div>
                </div>

                <v-expand-transition>
                    <div v-show="expandedStages[stage.key]">
                        <draggable 
                            :list="stage.companies"
                            group="companies"
                            @start="onDragStart"
                            @end="onDragEnd"
                            @change="onStageChange($event, stage.key)"
                            class="stage-drop-zone"
                            :animation="200"
                            ghost-class="sortable-ghost"
                            drag-class="sortable-drag"
                        >
                            <div 
                                v-for="company in stage.companies" 
                                :key="company.id"
                                class="company-card-wrapper"
                                :data-company-id="company.id"
                            >
                                    <v-card class="company-card-new" elevation="0" @click="viewCompany(company)">
                                        <v-card-text class="pa-6">
                                            <!-- Header with Company Name and Status Chips -->
                                            <div class="d-flex align-center justify-space-between mb-4">
                                                <div class="d-flex align-center">
                                                    <div class="company-icon-new mr-3">
                                                        <v-icon color="primary" size="40">mdi-domain</v-icon>
                                                    </div>
                                                    <div>
                                                        <h2 class="company-name-new">{{ company.name }}</h2>
                                                    </div>
                                                </div>
                                                <div class="d-flex flex-column align-end" style="gap: 8px;">
                                                    <v-chip 
                                                        small 
                                                        :color="getCompanyStatusColor(company.status)" 
                                                        dark
                                                        class="status-chip">
                                                        {{ company.status || 'Active' }}
                                                    </v-chip>
                                                    <v-chip 
                                                        small 
                                                        :color="getProgressStatusColor(getCompanyProgress(company.id))" 
                                                        dark
                                                        class="status-chip">
                                                        <v-icon left small>{{ getProgressStatusIcon(getCompanyProgress(company.id)) }}</v-icon>
                                                        {{ getProgressStatusText(company.id) }}
                                                    </v-chip>
                                                </div>
                                            </div>

                                            <!-- Description -->
                                            <p class="company-description-new mb-4">
                                                {{ company.description || 'No description' }}
                                            </p>

                                            <!-- Website -->
                                            <div class="info-row mb-6">
                                                <v-icon small color="grey darken-1" class="mr-2">mdi-web</v-icon>
                                                <a v-if="company.website" :href="company.website" target="_blank" @click.stop class="website-link">
                                                    {{ company.website }}
                                                </a>
                                                <span v-else class="info-text">No website available</span>
                                            </div>

                                            <!-- Goals and Development Progress Section -->
                                            <v-row class="mb-4">
                                                <!-- Goals -->
                                                <v-col cols="6">
                                                    <div class="info-section">
                                                        <div class="section-header-new mb-2">
                                                            <v-icon small color="grey darken-1">mdi-target</v-icon>
                                                            <span class="ml-1 grey--text text--darken-1">Goals</span>
                                                        </div>
                                                        <div class="big-number">{{ getCompanyGoalCount(company.id) }}</div>
                                                        <div class="sub-text">{{ getCompanyGoals(company.id) }}</div>
                                                    </div>
                                                </v-col>

                                                <!-- Development Progress -->
                                                <v-col cols="6">
                                                    <div 
                                                        class="info-section development-clickable" 
                                                        @click.stop="openEditProgress(company)"
                                                    >
                                                        <!-- Stage Progress (auto-calculated) -->
                                                        <div class="section-header-new mb-1">
                                                            <span class="grey--text text--darken-1" style="font-size:11px;">Stage Progress</span>
                                                            <span class="ml-auto primary--text font-weight-bold" style="font-size:11px;">{{ Math.round(getStageProgress(company.id)) }}%</span>
                                                        </div>
                                                        <v-progress-linear 
                                                            :value="getStageProgress(company.id)" 
                                                            height="6" 
                                                            rounded
                                                            color="primary"
                                                            class="mb-3"></v-progress-linear>
                                                        <!-- Completion (manual) -->
                                                        <div class="section-header-new mb-1">
                                                            <span class="grey--text text--darken-1" style="font-size:11px;">Completion</span>
                                                            <span class="ml-auto success--text font-weight-bold" style="font-size:11px;">{{ Math.round(getCompanyProgress(company.id)) }}%</span>
                                                        </div>
                                                        <v-progress-linear 
                                                            :value="getCompanyProgress(company.id)" 
                                                            height="6" 
                                                            rounded
                                                            color="success"
                                                            class="mb-1"></v-progress-linear>
                                                    </div>
                                                </v-col>
                                            </v-row>

                                            <!-- Due Date and Stage Section (below Goals) -->
                                            <div class="d-flex align-center mb-4" style="gap: 24px;">
                                                <!-- Due Date (Target Date) -->
                                                <div class="info-row">
                                                    <v-icon small color="grey darken-1" class="mr-1">mdi-calendar</v-icon>
                                                    <span v-if="getCompanyTargetDate(company.id)" class="info-text">Due: {{ formatDate(getCompanyTargetDate(company.id)) }}</span>
                                                    <span v-else class="info-text grey--text">No due date</span>
                                                    <span v-if="getCompanyTargetDate(company.id) && $date().isAfter($date(getCompanyTargetDate(company.id)))" class="ml-2 error--text">
                                                        Overdue by {{ $date().diff($date(getCompanyTargetDate(company.id)), 'day') }} days
                                                    </span>
                                                    <span v-else-if="getCompanyTargetDate(company.id) && $date(getCompanyTargetDate(company.id)).diff($date(), 'day') <= 7" class="ml-2 warning--text">
                                                        {{ $date(getCompanyTargetDate(company.id)).diff($date(), 'day') }} days left
                                                    </span>
                                                    <span v-else-if="getCompanyTargetDate(company.id)" class="ml-2 grey--text">
                                                        {{ $date(getCompanyTargetDate(company.id)).diff($date(), 'day') }} days left
                                                    </span>
                                                </div>

                                                <!-- Stage -->
                                                <div v-if="getCompanyStage(company.id)" class="info-row">
                                                    <span class="info-text">{{ getCompanyStage(company.id) }}</span>
                                                </div>
                                            </div>

                                            <!-- Team Members -->
                                            <div class="team-section">
                                                <div class="section-header-new mb-3">
                                                    <v-icon small color="grey darken-1">mdi-account-group</v-icon>
                                                    <span class="ml-1 grey--text text--darken-1">Team Members ({{ getTeamMemberCount(company) }})</span>
                                                </div>
                                                <div v-if="getTeamMemberCount(company) > 0" class="team-chips">
                                                    <v-chip 
                                                        v-for="(memberId, index) in getCompanyTeamMembers(company.id).slice(0, 5)"
                                                        :key="`member-${company.id}-${memberId}-${index}`" 
                                                        small 
                                                        class="mr-2 mb-2">
                                                        {{ getUserFullName(memberId) }}
                                                    </v-chip>
                                                    <v-chip 
                                                        v-if="getTeamMemberCount(company) > 5" 
                                                        small 
                                                        color="grey lighten-2"
                                                        class="mb-2">
                                                        +{{ getTeamMemberCount(company) - 5 }} more
                                                    </v-chip>
                                                </div>
                                                <div v-else class="no-members-text">
                                                    No members yet
                                                </div>
                                            </div>
                                        </v-card-text>
                                    </v-card>
                                </div>
                            
                            <!-- Empty state shown when no companies -->
                            <div v-if="stage.count === 0" class="empty-placeholder">
                                <p class="grey--text text-center">Drag companies here</p>
                            </div>
                        </draggable>
                    </div>
                </v-expand-transition>
            </div>
        </div>

        <!-- Create Company Dialog -->
        <CreateCompanyDialog v-model="createCompanyDialog" @company-created="onCompanyCreated" />

        <!-- Edit Progress Dialog -->
        <v-dialog v-model="editProgressDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title class="d-flex justify-space-between align-center">
                    <span>Edit Progress</span>
                    <v-btn icon @click="closeEditProgress">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="pt-4">
                    <!-- Stage -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 mb-2 d-block">Stage</label>
                        <v-select
                            v-model="editProgress.stage"
                            :items="stageOptions"
                            item-text="text"
                            item-value="text"
                            outlined
                            dense
                            hide-details
                            @change="onStageSelectionChange"
                        >
                            <template v-slot:selection="{ item }">
                                <span>{{ item.text }}</span>
                            </template>
                            <template v-slot:item="{ item }">
                                <div 
                                    class="stage-item" 
                                    :style="{ backgroundColor: item.color }"
                                    :class="{ 'stage-item-disabled': item.disabled, 'stage-item-selected': editProgress.stage === item.text }"
                                >
                                    <v-icon 
                                        small 
                                        :color="item.iconColor"
                                        class="mr-2"
                                    >
                                        {{ item.icon }}
                                    </v-icon>
                                    <span :class="{ 'grey--text': item.disabled }">{{ item.text }}</span>
                                    <v-icon v-if="editProgress.stage === item.text" small class="ml-auto">
                                        mdi-check
                                    </v-icon>
                                </div>
                            </template>
                        </v-select>
                    </div>

                    <!-- Stage Progress (read-only, auto-calculated) -->
                    <div class="mb-4">
                        <div class="d-flex justify-space-between align-center mb-2">
                            <label class="text-subtitle-2">Stage Progress</label>
                            <span class="text-subtitle-1 font-weight-bold primary--text">{{ selectedCompanyForEdit ? Math.round(getStageProgress(selectedCompanyForEdit.id)) : 0 }}%</span>
                        </div>
                        <v-progress-linear
                            :value="selectedCompanyForEdit ? getStageProgress(selectedCompanyForEdit.id) : 0"
                            height="10"
                            rounded
                            color="primary"
                            background-color="grey lighten-3"
                        ></v-progress-linear>
                        <div class="caption grey--text mt-1">Auto-calculated based on current stage ({{ (100/7).toFixed(1) }}% per stage)</div>
                    </div>

                    <!-- Target Date -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 mb-2 d-block">Target Date</label>
                        <v-menu
                            v-model="dateMenu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    :value="formattedTargetDate"
                                    outlined
                                    dense
                                    hide-details
                                    placeholder="Set target date"
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                    clearable
                                    @click:clear="editProgress.targetDate = null"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="editProgress.targetDate"
                                @input="dateMenu = false"
                                :min="new Date().toISOString().substr(0, 10)"
                                color="primary"
                            ></v-date-picker>
                        </v-menu>
                    </div>
                </v-card-text>
                <v-card-actions class="px-6 pb-6">
                    <v-btn
                        color="primary"
                        block
                        large
                        @click="saveProgress"
                    >
                        <v-icon left>mdi-check</v-icon>
                        Save Changes
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Stage Transition Warning Dialog -->
        <v-dialog v-model="stageWarningDialog" max-width="600px" persistent>
            <v-card>
                <v-card-title class="d-flex align-center">
                    <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
                    <span>Unusual Stage Transition</span>
                </v-card-title>
                <v-card-text class="pt-4">
                    <div class="mb-4">
                        <div class="text-subtitle-1 font-weight-medium mb-2">Company: {{ stageTransition.company?.name }}</div>
                        
                        <!-- Stage Transition Visualization with Step Numbers -->
                        <div class="d-flex align-center justify-center my-4">
                            <v-chip color="blue lighten-4" class="px-4">
                                {{ stageTransition.fromStage || 'Uncategorised' }}
                                <span v-if="stageTransition.fromStageNumber > 0" class="ml-2 text-caption">(Step {{ stageTransition.fromStageNumber }})</span>
                            </v-chip>
                            <v-icon class="mx-2">mdi-arrow-right</v-icon>
                            <v-chip color="cyan lighten-4" class="px-4">
                                {{ stageTransition.toStage || 'Uncategorised' }}
                                <span v-if="stageTransition.toStageNumber > 0" class="ml-2 text-caption">(Step {{ stageTransition.toStageNumber }})</span>
                            </v-chip>
                        </div>

                        <!-- Warning Messages -->
                        <div v-if="stageTransition.isBackward" class="mb-3">
                            <v-alert type="warning" dense text class="mb-2">
                                <strong>Moving backward:</strong> This transition goes back {{ Math.abs(stageTransition.toStageNumber - stageTransition.fromStageNumber) }} step{{ Math.abs(stageTransition.toStageNumber - stageTransition.fromStageNumber) > 1 ? 's' : '' }}. Are you sure you want to proceed?
                            </v-alert>
                        </div>

                        <div v-else-if="stageTransition.skippedStages > 0" class="mb-3">
                            <v-alert type="warning" dense text class="mb-2">
                                <strong>Skipping stages:</strong> This transition skips {{ stageTransition.skippedStages }} stage{{ stageTransition.skippedStages > 1 ? 's' : '' }}. Are you sure you want to proceed?
                            </v-alert>
                        </div>

                        <div v-if="stageTransition.currentProgress < 100" class="mb-3">
                            <v-alert type="warning" dense text class="mb-2">
                                Current progress is {{ stageTransition.currentProgress }}%. Moving forward without 100% completion may cause issues.
                            </v-alert>
                        </div>

                        <!-- Reason Input -->
                        <div class="mt-4">
                            <label class="text-subtitle-2 font-weight-medium mb-2 d-block">Reason for transition (optional)</label>
                            <v-textarea
                                v-model="stageTransition.reason"
                                placeholder="Why is this transition needed?"
                                outlined
                                dense
                                rows="3"
                                hide-details
                            ></v-textarea>
                        </div>
                    </div>
                </v-card-text>
                <v-card-actions class="px-6 pb-4">
                    <v-btn text @click="cancelStageTransition">
                        Cancel
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="warning" @click="proceedWithStageTransition">
                        Proceed Anyway
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import CreateCompanyDialog from './dialogs/CreateCompanyDialog.vue';
import draggable from 'vuedraggable';

export default {
    name: 'ProjectsComponent',
    components: {
        CreateCompanyDialog,
        draggable
    },
    inject: [
        'users',
        'companies',
        'statuses',
        'refreshCompanies',
        'goals',
        'refreshGoals',
        'milestones',
        'refreshMilestones',
        'overviewTasks',
        'personalTasks',
        'assignedTasks',
        'bottlenecks',
        'refreshBottlenecks',
        'developments',
        'refreshDevelopments',
        'createDevelopment',
        'updateDevelopment',
        'getCompanyDevelopment'
    ],
    data() {
        return {
            createCompanyDialog: false,
            searchQuery: '',
            showAllBottlenecks: false, // Toggle for showing all bottlenecks
            initialLoading: true, // Loading state for initial page load
            loadingCompanies: true,
            loadingGoals: true,
            loadingBottlenecks: true,
            loadingDevelopments: true,
            refreshing: false,
            editProgressDialog: false,
            selectedCompanyForEdit: null,
            dateMenu: false,
            expandedStages: {}, // Track which stages are expanded
            companiesByStageData: [], // Mutable version for drag and drop
            companyCompletionMap: {}, // company_id => completion_percentage (from single API call)
            isDragging: false, // Track active drag operations
            isUpdatingStage: false, // Track when stage update is in progress
            stageWarningDialog: false,
            stageTransition: {
                company: null,
                fromStage: '',
                toStage: '',
                fromStageNumber: 0,
                toStageNumber: 0,
                skippedStages: 0,
                isBackward: false,
                currentProgress: 0,
                reason: '',
                event: null,
                isFromDialog: false
            },
            editProgress: {
                stage: '',
                progressType: '',
                progress: 0,
                targetDate: null
            },
            stageOptions: [
                { text: 'Planned', icon: 'mdi-check-circle', color: '#e8f5e9', iconColor: '#4caf50', disabled: false },
                { text: 'In Development', icon: 'mdi-check', color: '#e8f5e9', iconColor: '#4caf50', disabled: false },
                { text: 'Launch Ready', icon: 'mdi-check-circle', color: '#e8f5e9', iconColor: '#4caf50', disabled: false },
                { text: 'Soft Launch', icon: 'mdi-alert', color: '#fff3e0', iconColor: '#ff9800', disabled: false },
                { text: 'Beta', icon: 'mdi-alert', color: '#fff3e0', iconColor: '#ff9800', disabled: false },
                { text: 'Live & Scaling', icon: 'mdi-close-circle', color: '#fafafa', iconColor: '#bdbdbd', disabled: true },
                { text: 'On Hold', icon: 'mdi-check-circle', color: '#e8f5e9', iconColor: '#4caf50', disabled: false },
                { text: 'Archived', icon: 'mdi-check-circle', color: '#e8f5e9', iconColor: '#4caf50', disabled: false }
            ]
        };
    },
    computed: {
        ...mapGetters({
            get_user: 'auth/get_user'
        }),
        canCreateCompany() {
            // Allow specific user IDs
            const privilegedUserIds = [1, 177, 145];
            if (this.get_user && privilegedUserIds.includes(this.get_user.id)) {
                return true;
            }
            
            // Allow specific role IDs (1=Admin, 2=Manager)
            const allowedRoleIds = [1, 2];
            return this.get_user && allowedRoleIds.includes(this.get_user.role_id);
        },
        accessibleCompanies() {
            const companiesList = this.companies();
            if (!Array.isArray(companiesList)) return [];

            // Exception: Specific user IDs can see all companies
            const privilegedUserIds = [1, 177, 145];
            if (this.get_user && privilegedUserIds.includes(this.get_user.id)) {
                return companiesList;
            }

            // Filter companies based on user access
            return companiesList.filter(company => this.hasAccessToCompany(company));
        },
        totalCompanies() {
            return this.accessibleCompanies.length;
        },
        activeGoals() {
            const goalsList = this.goals();
            if (!Array.isArray(goalsList)) return 0;
            
            // Filter goals for accessible companies only
            const accessibleCompanyIds = this.accessibleCompanies.map(c => c.id);
            
            return goalsList.filter(g => {
                // Check if goal belongs to an accessible company
                if (g.company_id && !accessibleCompanyIds.includes(g.company_id)) {
                    return false;
                }
                
                const status = g.status ? g.status.toLowerCase() : '';
                return status === 'active' || status === 'planning' || status === 'in progress' || status === 'ongoing';
            }).length;
        },
        allBottlenecks() {
            const bottlenecksList = this.bottlenecks();
            if (!Array.isArray(bottlenecksList)) return [];
            
            // Filter bottlenecks for accessible companies only
            const accessibleCompanyIds = this.accessibleCompanies.map(c => c.id);
            
            return bottlenecksList.filter(bottleneck => {
                // Check if bottleneck belongs to an accessible company
                if (bottleneck.company_id && !accessibleCompanyIds.includes(bottleneck.company_id)) {
                    return false;
                }
                return true;
            });
        },
        bottlenecks() {
            // Return first 3 or all depending on showAllBottlenecks
            return this.showAllBottlenecks ? this.allBottlenecks : this.allBottlenecks.slice(0, 3);
        },
        filteredCompanies() {
            if (!this.searchQuery) return this.accessibleCompanies;

            const query = this.searchQuery.toLowerCase();
            return this.accessibleCompanies.filter(company =>
                company.name.toLowerCase().includes(query) ||
                (company.description && company.description.toLowerCase().includes(query))
            );
        },
        formattedTargetDate() {
            if (!this.editProgress.targetDate) return '';
            return this.$date(this.editProgress.targetDate).format('MMMM DD, YYYY');
        },
        timelineStages() {
            // Return only the main progression stages (excluding On-hold and Archived)
            return this.companiesByStageData.filter(stage => 
                stage.key !== 'On-hold' && 
                stage.key !== 'Archived' && 
                stage.key !== null
            );
        },
        totalInProgressCompanies() {
            // Count companies that are not On-hold, Archived, or Uncategorised
            return this.companiesByStageData
                .filter(stage => stage.key !== 'On-hold' && stage.key !== 'Archived' && stage.key !== null)
                .reduce((sum, stage) => sum + stage.count, 0);
        },
        developmentsList() {
            // Computed property that calls the injected function
            return this.developments() || [];
        },
        progressTypeOptions() {
            // Return progress type options based on the selected stage
            const stageProgressMap = {
                'Planned': ['Planning', 'Requirements', 'Scoping'],
                'In Development': ['Development', 'Content', 'Design'],
                'Launch Ready': ['Marketing', 'Deployment', 'Testing'],
                'Soft Launch': ['Limited Rollout', 'Early Access', 'Feedback Collection'],
                'Beta': ['Beta Testing', 'Bug Fixing', 'Performance Tuning'],
                'Live & Scaling': ['Operations', 'Growth', 'Optimizations'],
                'On-hold': ['Paused'],
                'Archived': ['Completed', 'Cancelled']
            };
            return stageProgressMap[this.editProgress.stage] || ['Development', 'Content', 'Design'];
        }
    },
    watch: {
        filteredCompanies: {
            handler() {
                if (!this.isDragging && !this.isUpdatingStage) {
                    this.buildCompaniesByStage();
                }
            },
            immediate: true
        },
        developmentsList: {
            handler(newVal) {
                console.log('[Watcher:developmentsList] Triggered, count:', newVal?.length, 'isDragging:', this.isDragging, 'isUpdatingStage:', this.isUpdatingStage);
                if (!this.isDragging && !this.isUpdatingStage) {
                    this.buildCompaniesByStage();
                }
            },
            deep: true
        }
    },
    async mounted() {
        // Initialize all stages as collapsed; stages with companies will be expanded after build
        this.expandedStages = {
            'Planned': false,
            'In Development': false,
            'Launch Ready': false,
            'Soft Launch': false,
            'Beta': false,
            'Live & Scaling': false,
            'On-hold': false,
            'Archived': false,
            null: false // Uncategorised
        };

        // Companies are already loaded by DefaultLayout, just set loading to false
        this.loadingCompanies = false;

        // Check if other data needs to be loaded
        const hasBottlenecks = (this.bottlenecks() || []).length > 0;
        const hasGoals = (this.goals() || []).length > 0;
        const hasDevelopments = (this.developments() || []).length > 0;

        if (!hasBottlenecks) {
            this.loadingBottlenecks = true;
            await this.refreshBottlenecks();
        }
        this.loadingBottlenecks = false;

        if (!hasGoals) {
            this.loadingGoals = true;
            await this.refreshGoals();
        }
        this.loadingGoals = false;

        if (!hasDevelopments) {
            this.loadingDevelopments = true;
            await this.refreshDevelopments();
        }
        this.loadingDevelopments = false;

        // Always refresh goals to ensure counts are up-to-date when navigating back
        await this.refreshGoals();

        // Load goal tasks in background for completion calculation
        this.loadGoalTasks();

        // Build companies by stage after all data is loaded
        this.$nextTick(() => {
            this.buildCompaniesByStage();
            // Hide initial loading overlay
            this.initialLoading = false;
        });
    },
    methods: {
        async loadGoalTasks() {
            try {
                const response = await this.$axios.get('api/goals-v2/company-completion');
                if (response.data && response.data.response) {
                    const map = {};
                    (response.data.data || []).forEach(row => {
                        map[row.company_id] = row.completion_percentage;
                    });
                    this.companyCompletionMap = map;
                }
            } catch (e) {
                // silently fail
            }
        },
        buildCompaniesByStage() {
            console.log('[BuildCompaniesByStage] Starting rebuild, filtered companies count:', this.filteredCompanies.length);
            console.log('[BuildCompaniesByStage] Developments count:', this.developmentsList.length);
            
            const stages = [
                { 
                    name: 'Planned', 
                    key: 'Planned', 
                    color: '#9c27b0', 
                    icon: 'mdi-map-marker-path',
                    progressStages: ['Planning', 'Requirements', 'Scoping']
                },
                { 
                    name: 'In Development', 
                    key: 'In Development', 
                    color: '#2196f3', 
                    icon: 'mdi-code-braces',
                    progressStages: ['Development', 'Content', 'Design']
                },
                { 
                    name: 'Launch Ready', 
                    key: 'Launch Ready', 
                    color: '#ff9800', 
                    icon: 'mdi-rocket-launch',
                    progressStages: ['Marketing', 'Deployment', 'Testing']
                },
                { 
                    name: 'Soft Launch', 
                    key: 'Soft Launch', 
                    color: '#4caf50', 
                    icon: 'mdi-rocket',
                    progressStages: ['Limited Rollout', 'Early Access', 'Feedback Collection']
                },
                { 
                    name: 'Beta', 
                    key: 'Beta', 
                    color: '#00bcd4', 
                    icon: 'mdi-test-tube',
                    progressStages: ['Beta Testing', 'Bug Fixing', 'Performance Tuning']
                },
                { 
                    name: 'Live & Scaling', 
                    key: 'Live & Scaling', 
                    color: '#4caf50', 
                    icon: 'mdi-trending-up',
                    progressStages: ['Operations', 'Growth', 'Optimizations']
                },
                { 
                    name: 'On-hold', 
                    key: 'On-hold', 
                    color: '#ff9800', 
                    icon: 'mdi-pause-circle',
                    progressStages: ['Paused']
                },
                { 
                    name: 'Archived', 
                    key: 'Archived', 
                    color: '#9e9e9e', 
                    icon: 'mdi-archive',
                    progressStages: ['Completed', 'Cancelled']
                },
                { name: 'Uncategorised', key: null, color: '#f44336', icon: 'mdi-alert-circle', progressStages: [] }
            ];

            this.companiesByStageData = stages.map(stage => {
                const companies = this.filteredCompanies.filter(company => {
                    const development = this.getCompanyDevelopment(company.id);
                    const companyStage = development ? development.stage : null;
                    
                    if (stage.key === null) {
                        return !companyStage;
                    }
                    return companyStage === stage.key;
                });

                return {
                    ...stage,
                    companies,
                    count: companies.length
                };
            });

            // Expand stages that have companies; collapse empty stages
            this.companiesByStageData.forEach(stage => {
                if (stage.companies.length > 0) {
                    this.$set(this.expandedStages, stage.key, true);
                } else if (!(stage.key in this.expandedStages)) {
                    this.$set(this.expandedStages, stage.key, false);
                }
            });
        },
        hasAccessToCompany(company) {
            // Exception: Specific user IDs can see all companies
            const privilegedUserIds = [1, 177, 145];
            if (this.get_user && privilegedUserIds.includes(this.get_user.id)) {
                return true;
            }

            if (!this.get_user) return false;

            // Grant access if user has explicit company access
            if (this.get_user.access && Array.isArray(this.get_user.access)) {
                if (this.get_user.access.some(access => access.company_id === company.id)) {
                    return true;
                }
            }

            // Grant access if user is assigned to any task in this company
            const userId = this.get_user.id;
            const assigned = this.assignedTasks ? this.assignedTasks() : [];
            const personal = this.personalTasks ? this.personalTasks() : [];
            const allTasks = [
                ...(Array.isArray(assigned) ? assigned : []),
                ...(Array.isArray(personal) ? personal : []),
            ];
            return allTasks.some(task => {
                if (task.company_id !== company.id) return false;
                // Check assignees array
                if (Array.isArray(task.assignees)) {
                    return task.assignees.some(
                        a => a.user_id === userId || (a.user && a.user.id === userId)
                    );
                }
                return false;
            });
        },
        async refreshData() {
            this.refreshing = true;
            try {
                await Promise.all([
                    this.refreshCompanies(),
                    this.refreshBottlenecks(),
                    this.refreshGoals(),
                    this.refreshDevelopments()
                ]);
                this.$toast.success('Data refreshed successfully');
            } catch (error) {
                console.error('Error refreshing data:', error);
                this.$toast.error('Failed to refresh data');
            } finally {
                this.refreshing = false;
            }
        },
        openCreateCompanyDialog() {
            this.createCompanyDialog = true;
        },
        async onCompanyCreated() {
            // Refresh companies list
            await this.refreshCompanies();
            this.$toast.success('Company created successfully');
        },
        viewCompany(company) {
            // Navigate to company detail view or open dialog
            this.$router.push({ name: 'ViewProject', params: { id: company.id } });
        },
        getAssigneeNames(assignees) {
            if (!assignees || !Array.isArray(assignees) || assignees.length === 0) {
                return 'Unassigned';
            }

            // Map assignees to their names
            const names = assignees.map(assignee => {
                if (assignee.user && assignee.user.name) {
                    return assignee.user.name;
                }
                return null;
            }).filter(name => name !== null);

            if (names.length === 0) return 'Unassigned';
            if (names.length === 1) return names[0];
            if (names.length === 2) return names.join(' and ');

            // More than 2 assignees: show first name and count
            return `${names[0]} +${names.length - 1} others`;
        },
        getUserInitials(userId) {
            const usersList = this.users();
            if (!Array.isArray(usersList)) return '??';
            const user = usersList.find(u => u.id === userId);
            if (!user || !user.name) return '??';
            const names = user.name.split(' ');
            return names.map(n => n[0]).join('').substring(0, 2).toUpperCase();
        },
        getTeamMemberCount(company) {
            // Prefer team_members_count from API if available
            if (company.team_members_count !== undefined && company.team_members_count !== null) {
                return company.team_members_count;
            }
            // Fallback to calculating from actual team members array
            return this.getCompanyTeamMembers(company.id).length;
        },
        getUserFullName(userId) {
            const usersList = this.users();
            if (!Array.isArray(usersList)) return 'Unknown User';

            // First try to find in the users list
            let user = usersList.find(u => u.id === userId);
            if (user && user.name) return user.name;

            // Fallback: search in company team_members
            const companiesList = this.companies();
            if (Array.isArray(companiesList)) {
                for (const company of companiesList) {
                    if (company.team_members && Array.isArray(company.team_members)) {
                        const teamMember = company.team_members.find(tm => tm.user_id === userId);
                        if (teamMember && teamMember.user && teamMember.user.name) {
                            return teamMember.user.name;
                        }
                    }
                }
            }

            return 'Unknown User';
        },
        getCompanyName(task) {
            // First check if company name is directly available on the task
            if (task.company && task.company.name) {
                return task.company.name;
            }

            // Fallback: lookup company by company_id
            if (task.company_id) {
                const companiesList = this.companies();
                if (Array.isArray(companiesList)) {
                    const company = companiesList.find(c => c.id === task.company_id);
                    if (company && company.name) {
                        return company.name;
                    }
                }
            }

            return 'Unknown Company';
        },
        formatDate(date) {
            if (!date) return 'No date';
            return this.$date(date).format('MMM D, YYYY');
        },
        getOverdueDays(dueDate) {
            if (!dueDate) return '';
            const days = this.$date().diff(this.$date(dueDate), 'day');
            return days > 0 ? `Overdue` : '';
        },
        getCompletedStatusId() {
            const statusList = this.statuses();
            if (!Array.isArray(statusList)) return null;
            const completedStatus = statusList.find(s =>
                s.name.toLowerCase() === 'completed' || s.name.toLowerCase() === 'done'
            );
            return completedStatus ? completedStatus.id : null;
        },
        getStatusText(statusId) {
            const statusList = this.statuses();
            if (!Array.isArray(statusList)) return 'Unknown';
            const status = statusList.find(s => s.id === statusId);
            return status ? status.name : 'Unknown';
        },
        getStatusColor(statusId) {
            const statusList = this.statuses();
            if (!Array.isArray(statusList)) return 'grey';
            const status = statusList.find(s => s.id === statusId);

            if (!status) return 'grey';

            const statusName = status.name.toLowerCase();
            if (statusName.includes('progress')) return 'primary';
            if (statusName.includes('stuck') || statusName.includes('block')) return 'error';
            return 'warning';
        },
        getCompanyStatusColor(status) {
            if (!status) return 'success';
            const statusLower = status.toLowerCase();
            if (statusLower === 'active') return 'success';
            if (statusLower === 'on-hold') return 'warning';
            if (statusLower === 'closed') return 'grey';
            return 'success';
        },
        getCompanyGoalCount(companyId) {
            const goalsList = this.goals();
            const goals = Array.isArray(goalsList) ? goalsList.filter(g => g.company_id === companyId) : [];
            return goals.length;
        },
        getCompanyGoals(companyId) {
            // Count goals for this company
            const goalsList = this.goals();
            const goals = Array.isArray(goalsList) ? goalsList.filter(g => g.company_id === companyId) : [];
            
            // Count active goals (multiple possible status values for active)
            const activeGoals = goals.filter(g => {
                const status = g.status ? g.status.toLowerCase() : '';
                return status === 'active' || status === 'planning' || status === 'in progress' || status === 'ongoing';
            }).length;
            
            // Count completed goals
            const completedGoals = goals.filter(g => {
                const status = g.status ? g.status.toLowerCase() : '';
                return status === 'completed' || status === 'done' || status === 'finished';
            }).length;
            
            return `${activeGoals} active · ${completedGoals} completed`;
        },
        getProgressStatusColor(progress) {
            if (progress >= 75) return 'success';
            if (progress >= 50) return 'info';
            if (progress >= 25) return 'warning';
            return 'grey';
        },
        getProgressStatusText(companyId) {
            // Check if development exists for this company
            const development = this.getCompanyDevelopment(companyId);
            if (development && development.stage) {
                return development.stage;
            }

            // Fallback: calculate based on progress percentage
            const progress = this.getCompanyProgress(companyId);
            if (progress >= 75) return 'On Target';
            if (progress >= 50) return 'In Progress';
            if (progress >= 25) return 'Behind';
            return 'Not Started';
        },
        getProgressStatusIcon(progress) {
            if (progress >= 75) return 'mdi-check-circle';
            if (progress >= 50) return 'mdi-progress-clock';
            if (progress >= 25) return 'mdi-alert-circle';
            return 'mdi-circle-outline';
        },
        openEditProgress(company) {
            this.selectedCompanyForEdit = company;
            
            // Check if development exists for this company
            const existingDevelopment = this.getCompanyDevelopment(company.id);
            
            if (existingDevelopment) {
                // Load existing development data
                this.editProgress = {
                    stage: existingDevelopment.stage || 'In Development',
                    progressType: existingDevelopment.progress_type || this.getDefaultProgressType(existingDevelopment.stage || 'In Development'),
                    progress: existingDevelopment.progress || 0,
                    targetDate: existingDevelopment.target_date || null
                };
            } else {
                // Default values for new development
                const rawDate = this.getCompanyRawDueDate(company.id);
                this.editProgress = {
                    stage: 'In Development',
                    progressType: this.getDefaultProgressType('In Development'),
                    progress: this.getCompanyProgress(company.id) || 0,
                    targetDate: rawDate
                };
            }
            
            this.editProgressDialog = true;
        },
        closeEditProgress() {
            this.editProgressDialog = false;
            this.selectedCompanyForEdit = null;
        },
        onStageSelectionChange(newStage) {
            // When user changes stage in the dropdown, load data for that stage if it exists
            if (!this.selectedCompanyForEdit) return;
            
            console.log('[StageChange] Loading data for stage:', newStage);
            
            // Check if development exists for this company AND this new stage
            const existingDevelopment = this.getCompanyDevelopmentByStage(
                this.selectedCompanyForEdit.id,
                newStage
            );
            
            if (existingDevelopment) {
                // Load existing data for this stage
                console.log('[StageChange] Found existing development:', existingDevelopment.id, 'Progress:', existingDevelopment.progress);
                this.editProgress.progressType = existingDevelopment.progress_type || this.getDefaultProgressType(newStage);
                this.editProgress.progress = existingDevelopment.progress || 0;
                this.editProgress.targetDate = existingDevelopment.target_date || null;
            } else {
                // No existing development for this stage, reset to defaults
                console.log('[StageChange] No existing development for stage:', newStage, '- resetting to defaults');
                this.editProgress.progressType = this.getDefaultProgressType(newStage);
                this.editProgress.progress = 0;
                this.editProgress.targetDate = null;
            }
        },
        getDefaultProgressType(stage) {
            // Get the first progress type for the given stage
            const stageProgressMap = {
                'Planned': 'Planning',
                'In Development': 'Development',
                'Launch Ready': 'Marketing',
                'Soft Launch': 'Limited Rollout',
                'Beta': 'Beta Testing',
                'Live & Scaling': 'Operations',
                'On-hold': 'Paused',
                'Archived': 'Completed'
            };
            return stageProgressMap[stage] || 'Development';
        },
        async saveProgress() {
            if (!this.selectedCompanyForEdit) {
                this.$toast.error('No company selected');
                return;
            }

            // Get current stage and new stage
            const existingDevelopment = this.getCompanyDevelopment(this.selectedCompanyForEdit.id);
            const fromStage = existingDevelopment?.stage || null;
            const toStage = this.editProgress.stage;
            const currentProgress = this.editProgress.progress;
            
            // Check if warning is needed (only if stage is changing)
            if (fromStage !== toStage) {
                const transitionInfo = this.calculateSkippedStages(fromStage, toStage);
                const needsWarning = transitionInfo.skipped > 0 || transitionInfo.isBackward || currentProgress < 100;
                
                if (needsWarning) {
                    // Store transition data and show warning
                    this.stageTransition = {
                        company: this.selectedCompanyForEdit,
                        fromStage,
                        toStage,
                        fromStageNumber: transitionInfo.fromStageNumber,
                        toStageNumber: transitionInfo.toStageNumber,
                        skippedStages: transitionInfo.skipped,
                        isBackward: transitionInfo.isBackward,
                        currentProgress,
                        reason: '',
                        event: null,
                        isFromDialog: true
                    };
                    this.stageWarningDialog = true;
                    return; // Don't proceed yet
                }
            }
            
            // No warning needed or just updating progress, proceed directly
            await this.performSaveProgress();
        },
        async performSaveProgress(transitionReason = null) {
            if (!this.selectedCompanyForEdit) {
                this.$toast.error('No company selected');
                return;
            }

            try {
                // Check if development exists for this company AND this specific stage
                const existingDevelopment = this.getCompanyDevelopmentByStage(
                    this.selectedCompanyForEdit.id,
                    this.editProgress.stage
                );

                // First, unset current_stage for all other developments of this company
                await this.unsetCurrentStageForCompany(this.selectedCompanyForEdit.id);

                const developmentData = {
                    company_id: this.selectedCompanyForEdit.id,
                    stage: this.editProgress.stage,
                    progress_type: this.editProgress.progressType,
                    progress: existingDevelopment ? this.editProgress.progress : 0, // Reset to 0 for new records
                    target_date: existingDevelopment ? this.editProgress.targetDate : null, // Reset to null for new records
                    transition_reason: transitionReason || null,
                    current_stage: true // Mark this as the current stage
                };

                console.log('[PerformSaveProgress] Development data to save:', JSON.stringify(developmentData, null, 2));

                if (existingDevelopment && existingDevelopment.id) {
                    // Update existing development for this stage
                    console.log('[PerformSaveProgress] UPDATE - Development ID:', existingDevelopment.id, 'Company:', this.selectedCompanyForEdit.id, 'Stage:', this.editProgress.stage, 'current_stage: true');
                    await this.updateDevelopment(existingDevelopment.id, developmentData);
                    this.$toast.success('Progress updated successfully');
                } else {
                    // Create new development record for this stage with progress = 0
                    console.log('[PerformSaveProgress] CREATE - Company:', this.selectedCompanyForEdit.id, 'Stage:', this.editProgress.stage, 'Progress: 0, current_stage: true');
                    await this.createDevelopment(developmentData);
                    this.$toast.success('Progress created successfully');
                }

                // Refresh developments from backend
                await this.refreshDevelopments();
                
                console.log('[PerformSaveProgress] Developments refreshed, closing dialog and rebuilding');
                
                // Close dialog first
                this.closeEditProgress();
                
                // Wait for next tick and rebuild companies by stage
                await this.$nextTick();
                console.log('[PerformSaveProgress] About to rebuild companies by stage');
                this.buildCompaniesByStage();
                console.log('[PerformSaveProgress] Rebuild complete');
            } catch (error) {
                console.error('Error saving progress:', error);
                this.$toast.error('Failed to save progress');
            }
        },
        toggleStage(stageKey) {
            this.$set(this.expandedStages, stageKey, !this.expandedStages[stageKey]);
        },
        onDragStart() {
            this.isDragging = true;
        },
        onDragEnd() {
            this.isDragging = false;
        },
        async onStageChange(event, toStage) {
            if (event.added) {
                const company = event.added.element;
                const development = this.getCompanyDevelopment(company.id);
                const fromStage = development?.stage || null;
                const currentProgress = this.getCompanyProgress(company.id);
                
                // Check if warning is needed
                const transitionInfo = this.calculateSkippedStages(fromStage, toStage);
                const needsWarning = transitionInfo.skipped > 0 || transitionInfo.isBackward || currentProgress < 100;
                
                if (needsWarning) {
                    // Store transition data and show warning
                    this.stageTransition = {
                        company,
                        fromStage,
                        toStage,
                        fromStageNumber: transitionInfo.fromStageNumber,
                        toStageNumber: transitionInfo.toStageNumber,
                        skippedStages: transitionInfo.skipped,
                        isBackward: transitionInfo.isBackward,
                        currentProgress,
                        reason: '',
                        event,
                        isFromDialog: false
                    };
                    this.stageWarningDialog = true;
                    
                    // Revert the drag and drop change temporarily (local only, no API call needed)
                    this.buildCompaniesByStage();
                } else {
                    // No warning needed, proceed directly
                    this.isUpdatingStage = true;
                    await this.updateCompanyStage(company, toStage);
                    this.isUpdatingStage = false;
                    
                    // Rebuild handled by updateCompanyStage method
                }
            }
        },
        async updateCompanyStage(company, newStage, transitionReason = null) {
            try {
                // Check if development exists for this company AND this specific stage
                const existingDevelopmentForStage = this.getCompanyDevelopmentByStage(company.id, newStage);
                
                // First, unset current_stage for all other developments of this company
                await this.unsetCurrentStageForCompany(company.id);

                const developmentData = {
                    company_id: company.id,
                    stage: newStage || 'In Development',
                    progress_type: existingDevelopmentForStage?.progress_type || 'Development',
                    progress: existingDevelopmentForStage?.progress || 0, // Use existing or default to 0
                    target_date: existingDevelopmentForStage?.target_date || null, // Use existing or default to null
                    transition_reason: transitionReason || null,
                    current_stage: true // Mark this as the current stage
                };

                if (existingDevelopmentForStage && existingDevelopmentForStage.id) {
                    // Update existing development for this stage
                    console.log('[UpdateCompanyStage] UPDATE - Development ID:', existingDevelopmentForStage.id, 'Company:', company.id, 'Stage:', newStage, 'current_stage: true');
                    await this.updateDevelopment(existingDevelopmentForStage.id, developmentData);
                } else {
                    // Create new development record for this stage with progress = 0, target_date = null
                    console.log('[UpdateCompanyStage] CREATE - Company:', company.id, 'Stage:', newStage, 'Progress: 0, Target: null, current_stage: true');
                    await this.createDevelopment(developmentData);
                }

                // Refresh data and rebuild UI
                await this.refreshDevelopments();
                this.buildCompaniesByStage();
                this.$toast.success(`${company.name} moved to ${newStage || 'Uncategorised'}`);
            } catch (error) {
                console.error('Error updating company stage:', error);
                this.$toast.error('Failed to update stage');
                
                // ROLLBACK: Restore original state on error
                await this.refreshDevelopments();
                this.buildCompaniesByStage();
            }
        },
        getStageProgress(companyId) {
            // Auto-calculated: 100% / 7 stages ≈ 14.3% per stage
            const stageOrder = [
                'Planned',
                'In Development',
                'Launch Ready',
                'Soft Launch',
                'Beta',
                'Live & Scaling',
                'On-hold'
            ];
            const development = this.getCompanyDevelopment(companyId);
            const stage = development ? development.stage : null;
            if (!stage) return 0;
            const idx = stageOrder.indexOf(stage);
            if (idx === -1) return 0; // Archived / Uncategorised
            return ((idx + 1) / stageOrder.length) * 100;
        },
        getCompanyProgress(companyId) {
            // Single endpoint returns pre-computed completion_percentage per company
            return this.companyCompletionMap[companyId] ?? 0;
        },
        getCompanyRawDueDate(companyId) {
            // Get the nearest deadline from goals in YYYY-MM-DD format for date picker
            const goalsList = this.goals();
            const goals = Array.isArray(goalsList) ? goalsList.filter(g => g.company_id === companyId && g.deadline) : [];
            if (goals.length === 0) return null;

            const sortedGoals = goals.sort((a, b) =>
                this.$date(a.deadline).isAfter(this.$date(b.deadline)) ? 1 : -1
            );
            return this.$date(sortedGoals[0].deadline).format('YYYY-MM-DD');
        },
        getCompanyTeamMembers(companyId) {
            // Get team members assigned to this company
            // This could be from tasks or company data
            const companiesList = this.companies();
            if (!Array.isArray(companiesList)) return [];

            const company = companiesList.find(c => c.id === companyId);
            if (company && company.team_members && Array.isArray(company.team_members)) {
                // Extract user_id from team_members array
                return company.team_members.map(tm => tm.user_id).filter(id => id);
            }

            // Fallback: get unique users from bottleneck tasks for this company
            const bottlenecksList = this.bottlenecks();
            const companyTasks = Array.isArray(bottlenecksList) ? bottlenecksList.filter(t => t.company_id === companyId) : [];
            const uniqueUsers = new Set();

            companyTasks.forEach(task => {
                if (task.assignees && Array.isArray(task.assignees)) {
                    task.assignees.forEach(assignee => {
                        if (assignee.user_id) {
                            uniqueUsers.add(assignee.user_id);
                        }
                    });
                }
            });

            return Array.from(uniqueUsers).filter(id => id);
        },
        getCompanyDevelopmentByStage(companyId, stage) {
            // Get development for specific company AND stage
            const developmentsList = this.developments();
            if (!Array.isArray(developmentsList)) return null;
            
            return developmentsList.find(d => 
                d.company_id === companyId && d.stage === stage
            );
        },
        async unsetCurrentStageForCompany(companyId) {
            // Get all developments for this company and set current_stage to false
            const developmentsList = this.developments();
            if (!Array.isArray(developmentsList)) return;
            
            const companyDevelopments = developmentsList.filter(d => d.company_id === companyId);
            
            // Update all developments for this company to set current_stage = false
            for (const dev of companyDevelopments) {
                if (dev.id && dev.current_stage) {
                    try {
                        await this.updateDevelopment(dev.id, {
                            ...dev,
                            current_stage: false
                        });
                        console.log('[UnsetCurrentStage] Set current_stage=false for Development ID:', dev.id);
                    } catch (error) {
                        console.error('[UnsetCurrentStage] Error updating development:', dev.id, error);
                    }
                }
            }
        },
        calculateSkippedStages(fromStage, toStage) {
            // Define the normal progression order
            const stageOrder = [
                'Planned',
                'In Development',
                'Launch Ready',
                'Soft Launch',
                'Beta',
                'Live & Scaling'
            ];
            
            const fromIndex = stageOrder.indexOf(fromStage);
            const toIndex = stageOrder.indexOf(toStage);
            
            // If either stage is not in the progression (e.g., 'On Hold', 'Archived'), return default
            if (fromIndex === -1 || toIndex === -1) {
                return {
                    skipped: 0,
                    isBackward: false,
                    fromStageNumber: fromIndex + 1,
                    toStageNumber: toIndex + 1
                };
            }
            
            const diff = toIndex - fromIndex;
            
            return {
                skipped: Math.abs(diff) - 1,
                isBackward: diff < 0,
                fromStageNumber: fromIndex + 1,
                toStageNumber: toIndex + 1
            };
        },
        async proceedWithStageTransition() {
            const { company, toStage, reason, isFromDialog } = this.stageTransition;
            
            if (isFromDialog) {
                // This transition came from the Edit Progress dialog
                await this.performSaveProgress(reason);
            } else {
                // This transition came from drag-and-drop
                this.isUpdatingStage = true;
                await this.updateCompanyStage(company, toStage, reason);
                this.isUpdatingStage = false;
                
                // Rebuild handled by updateCompanyStage method
            }
            
            // Close dialog and reset
            this.stageWarningDialog = false;
            this.stageTransition = {
                company: null,
                fromStage: '',
                toStage: '',
                fromStageNumber: 0,
                toStageNumber: 0,
                skippedStages: 0,
                isBackward: false,
                currentProgress: 0,
                reason: '',
                event: null,
                isFromDialog: false
            };
        },
        cancelStageTransition() {
            const isFromDialog = this.stageTransition.isFromDialog;
            
            // Just close the dialog and reset
            this.stageWarningDialog = false;
            this.stageTransition = {
                company: null,
                fromStage: '',
                toStage: '',
                fromStageNumber: 0,
                toStageNumber: 0,
                skippedStages: 0,
                isBackward: false,
                currentProgress: 0,
                reason: '',
                event: null,
                isFromDialog: false
            };
            
            // If this was from drag-and-drop, rebuild to revert changes
            // If from dialog, the dialog stays open (just closes warning)
            if (!isFromDialog) {
                this.buildCompaniesByStage();
            }
        },
        getCompanyStage(companyId) {
            // Get the stage from development
            const development = this.getCompanyDevelopment(companyId);
            if (!development || !development.stage) return null;

            // Get total stages count - we'll use a fixed value or calculate based on stageOptions
            const totalStages = this.stageOptions.filter(s => !s.disabled).length;
            const currentStageIndex = this.stageOptions.findIndex(s => s.text === development.stage);
            
            if (currentStageIndex === -1) return null;
            
            return `Stage ${currentStageIndex + 1}/${totalStages}`;
        },
        getCompanyTargetDate(companyId) {
            // First check the company's own due_date field
            const companies = this.companies ? this.companies() : [];
            const company = companies.find(c => c.id === companyId);
            if (company?.due_date) return company.due_date;

            // Fall back to the current development record's target_date
            const development = this.getCompanyDevelopment(companyId);
            return development?.target_date || null;
        },
        getDaysLeftText(companyId) {
            // Get the nearest deadline from goals
            const goalsList = this.goals();
            const goals = Array.isArray(goalsList) ? goalsList.filter(g => g.company_id === companyId && g.deadline) : [];
            if (goals.length === 0) return '';

            const sortedGoals = goals.sort((a, b) =>
                this.$date(a.deadline).isAfter(this.$date(b.deadline)) ? 1 : -1
            );
            
            const dueDate = this.$date(sortedGoals[0].deadline);
            const now = this.$date();
            const daysLeft = dueDate.diff(now, 'day');
            
            if (daysLeft < 0) {
                return `${Math.abs(daysLeft)} days overdue`;
            } else if (daysLeft === 0) {
                return 'Due today';
            } else if (daysLeft === 1) {
                return '1 day left';
            } else {
                return `${daysLeft} days left`;
            }
        },
        getDaysLeftClass(companyId) {
            // Get the nearest deadline from goals
            const goalsList = this.goals();
            const goals = Array.isArray(goalsList) ? goalsList.filter(g => g.company_id === companyId && g.deadline) : [];
            if (goals.length === 0) return '';

            const sortedGoals = goals.sort((a, b) =>
                this.$date(a.deadline).isAfter(this.$date(b.deadline)) ? 1 : -1
            );
            
            const dueDate = this.$date(sortedGoals[0].deadline);
            const now = this.$date();
            const daysLeft = dueDate.diff(now, 'day');
            
            if (daysLeft < 0) {
                return 'error--text font-weight-medium';
            } else if (daysLeft <= 2) {
                return 'orange--text font-weight-medium';
            } else {
                return 'grey--text';
            }
        }
    }
};
</script>

<style scoped>
/* Stats Cards */
.stat-card {
    border-radius: 12px;
    border: 1px solid #e0e0e0;
    background: white;
}

.stat-label {
    font-size: 14px;
    color: #666;
    margin-bottom: 8px;
}

.stat-value {
    font-size: 32px;
    font-weight: 700;
    color: #1a1a1a;
}

.stat-icon {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.primary-icon {
    background-color: #e3f2fd;
}

.success-icon {
    background-color: #f3e5f5;
}

.error-icon {
    background-color: #ffebee;
}

/* Bottlenecks Card */
.bottlenecks-card {
    border-radius: 12px;
    border: 1px solid #ffcdd2;
    background: #ffebee;
}

.bottlenecks-header {
    background-color: #ffebee;
    color: #c62828;
    font-weight: 600;
}

.bottlenecks-content {
    padding-top: 0;
}

.bottlenecks-scrollable {
    max-height: 400px;
    overflow-y: auto;
}

.bottleneck-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    margin-bottom: 8px;
    background: white;
    border-radius: 8px;
}

.bottleneck-item:last-child {
    margin-bottom: 0;
}

.bottleneck-info {
    flex-grow: 1;
}

.bottleneck-title {
    font-weight: 600;
    font-size: 15px;
    color: #1a1a1a;
    margin-bottom: 4px;
}

.bottleneck-meta {
    font-size: 13px;
    color: #666;
    display: flex;
    align-items: center;
}

.company-name-text {
    font-weight: 500;
    color: #1976d2;
}

/* Section Header */
.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.section-title {
    font-size: 20px;
    font-weight: 600;
    color: #1a1a1a;
}

.search-field {
    max-width: 300px;
}

/* Company Cards - New Design */
.company-card-new {
    border-radius: 16px;
    border: 1px solid #e0e0e0;
    transition: all 0.3s ease;
    cursor: pointer;
    background: white;
    height: 100%;
}

.company-card-new:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
    transform: translateY(-2px);
}

.company-icon-new {
    width: 64px;
    height: 64px;
    background-color: #e3f2fd;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.company-name-new {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0;
}

.status-chip {
    font-weight: 600;
    letter-spacing: 0.5px;
}

.company-description-new {
    color: #666;
    font-size: 0.95rem;
    line-height: 1.5;
    margin: 0;
    min-height: 48px;
}

.info-section {
    text-align: left;
}

.development-clickable {
    cursor: pointer;
    padding: 12px;
    margin: -12px;
    border-radius: 8px;
    transition: background-color 0.2s ease;
}

.development-clickable:hover {
    background-color: #f5f5f5;
}

.section-header-new {
    display: flex;
    align-items: center;
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.big-number {
    font-size: 2.5rem;
    font-weight: 700;
    color: #1a1a1a;
    line-height: 1;
    margin-bottom: 4px;
}

.progress-number {
    color: #667eea;
}

.sub-text {
    font-size: 0.85rem;
    color: #666;
}

.team-section {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e0e0e0;
}

.team-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.no-members-text {
    color: #999;
    font-size: 0.9rem;
}

/* Old company card classes (keep for compatibility) */
.company-card {
    border-radius: 12px;
    border: 1px solid #e0e0e0;
    background: white;
    transition: all 0.2s ease;
    cursor: pointer;
    height: 100%;
}

.company-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
}

.company-icon {
    width: 48px;
    height: 48px;
    background-color: #e3f2fd;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.company-name {
    font-size: 18px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 4px;
}

.company-description {
    font-size: 13px;
    color: #666;
    line-height: 1.5;
}

.company-stats {
    border-top: 1px solid #f0f0f0;
    border-bottom: 1px solid #f0f0f0;
    padding-top: 12px;
    padding-bottom: 12px;
}

.stats-label {
    font-size: 12px;
    color: #666;
    display: flex;
    align-items: center;
}

.stats-value {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
}

.progress-date {
    font-size: 11px;
    color: #666;
    display: flex;
    align-items: center;
    gap: 4px;
}

.team-members {
    padding-top: 12px;
}

.team-label {
    font-size: 12px;
    color: #666;
    display: flex;
    align-items: center;
}

.team-avatars {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.more-members {
    background-color: #e0e0e0 !important;
}

/* Stage Item Styles */
.stage-item {
    width: 100%;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    cursor: pointer;
    transition: opacity 0.2s ease;
}

.stage-item:hover:not(.stage-item-disabled) {
    opacity: 0.8;
}

.stage-item-disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.stage-item-selected {
    font-weight: 600;
}

/* Stage Section Styles */
.stage-section {
    margin-bottom: 24px;
}

.stage-header {
    padding: 16px 20px;
    background: white;
    border-radius: 8px;
    border-left: 4px solid;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: 12px;
}

.stage-header:hover {
    background: #f5f5f5;
    transform: translateX(2px);
}

/* Visual feedback when dragging - highlight all stage sections */
.sortable-chosen ~ .stage-section .stage-header,
body:has(.sortable-chosen) .stage-header {
    background: linear-gradient(90deg, rgba(25, 118, 210, 0.05) 0%, transparent 100%);
    border-left-width: 5px;
    padding-left: 15px;
}

.stage-title {
    font-size: 0.9rem;
    font-weight: 600;
    letter-spacing: 0.5px;
    margin: 0;
}

.stage-drop-zone {
    min-height: 100px;
    padding: 12px;
    border-radius: 8px;
    background: #fafafa;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    transition: all 0.3s ease;
    border: 2px dashed transparent;
}

/* Highlight drop zone when dragging over it */
.stage-drop-zone.sortable-drag-over {
    background: rgba(25, 118, 210, 0.08) !important;
    border: 2px dashed #1976d2 !important;
    box-shadow: inset 0 0 20px rgba(25, 118, 210, 0.1);
}

@media (max-width: 1400px) {
    .stage-drop-zone {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 960px) {
    .stage-drop-zone {
        grid-template-columns: 1fr;
    }
}

.company-card-wrapper {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    grid-column: span 1 !important;
    position: relative !important;
}

.company-card-wrapper[style] {
    width: 100% !important;
}

.company-card-new {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    box-sizing: border-box !important;
}

.company-card-new[style] {
    width: 100% !important;
}

.empty-stage {
    padding: 40px;
    text-align: center;
    background: #fafafa;
    border: 2px dashed #e0e0e0;
    border-radius: 8px;
    margin: 12px;
    min-height: 100px;
}

.empty-placeholder {
    grid-column: 1 / -1;
    padding: 60px 40px;
    text-align: center;
    background: rgba(0, 0, 0, 0.02);
    border: 2px dashed #e0e0e0;
    border-radius: 8px;
    margin: 8px 0;
}

.empty-placeholder p {
    margin: 0;
    font-size: 0.95rem;
    color: #999;
}

/* Dragging styles */
.sortable-ghost {
    opacity: 0.4;
}

.sortable-drag {
    cursor: move !important;
}

.sortable-drag .company-card-wrapper,
.sortable-drag .company-card-new {
    width: 400px !important;
    max-width: 400px !important;
}

@media (max-width: 1400px) {
    .sortable-drag .company-card-wrapper,
    .sortable-drag .company-card-new {
        width: 350px !important;
        max-width: 350px !important;
    }
}

@media (max-width: 960px) {
    .sortable-drag .company-card-wrapper,
    .sortable-drag .company-card-new {
        width: 300px !important;
        max-width: 300px !important;
    }
}

/* Stage Progression Timeline Styles */
.timeline-card {
    background: white;
    border-radius: 12px;
    border: 1px solid #e0e0e0;
}

.timeline-header {
    text-align: center;
}

.timeline-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #212121;
    margin-bottom: 8px;
}

.timeline-subtitle {
    font-size: 0.95rem;
    color: #757575;
    margin: 0;
}

.stage-timeline {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px 0;
    position: relative;
}

.timeline-stage-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.timeline-connector {
    width: 80px;
    height: 2px;
    background: #e0e0e0;
    margin: 0 -8px;
    position: relative;
    top: -28px;
}

.timeline-stage {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    transition: transform 0.2s ease;
    padding: 0 16px;
}

.timeline-stage:hover {
    transform: translateY(-4px);
}

.stage-circle {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: 3px solid;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 1.1rem;
    transition: all 0.3s ease;
    background: white;
    margin-bottom: 12px;
}

.stage-circle.has-companies {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.stage-circle:hover {
    transform: scale(1.1);
}

.stage-count {
    font-size: 1.1rem;
    font-weight: 700;
}

.stage-label {
    font-size: 0.85rem;
    font-weight: 500;
    text-align: center;
    max-width: 90px;
    line-height: 1.2;
    transition: color 0.2s ease;
}

.timeline-tooltip {
    min-width: 240px;
}

.stage-dropdown-menu {
    max-width: 400px;
}

.stage-company-item {
    transition: background-color 0.2s ease;
    cursor: pointer;
}

.stage-company-item:hover {
    background-color: #f5f5f5;
}

.stage-company-item .v-list-item__title {
    font-weight: 500;
}

@media (max-width: 1200px) {
    .timeline-connector {
        width: 60px;
    }
    
    .timeline-stage {
        padding: 0 12px;
    }
    
    .stage-circle {
        width: 48px;
        height: 48px;
        font-size: 1rem;
    }
    
    .stage-label {
        font-size: 0.8rem;
        max-width: 80px;
    }
}

/* Website, Due Date, and Stage Info Rows */
.info-row {
    display: flex;
    align-items: center;
    font-size: 0.9rem;
    color: #666;
}

.info-text {
    color: #666;
    font-size: 0.9rem;
}

.website-link {
    color: #1976d2;
    text-decoration: none;
    font-size: 0.9rem;
    transition: color 0.2s ease;
}

.website-link:hover {
    color: #1565c0;
    text-decoration: underline;
}
</style>
